// Era / time-hop data layer: the curated, accuracy-locked foundation that lets the
// trail spine show WHEN each source spoke and how far a hop travels in time.
//
// ACCURACY-LOCK + THEOLOGY (see docs/trail-adventure/SPEC.md s2):
//   - The Foundation guard keys off REF SHAPE, never the category string. Sefaria
//     returns category "Tanakh" for COMMENTARIES too (Rashi/Ramban/Or HaChaim), so
//     a base-vs-commentary split MUST come from the ref ("X on Book" has " on ").
//     All of Tanakh (Chumash + Navi + Ketuvim) is the undated wellspring; the
//     Torah's own academic compDate is structurally unreachable for a base ref.
//   - We date the WORK (Sefaria compDate), never silently substitute author lifespan.
//   - A null year is first-class ("unknown era"), never interpolated. hop() never
//     synthesizes a year from a tier/band midpoint - a number appears only when BOTH
//     sides carry a real exact/approximate year.
//   - eraOf() is pure / idempotent. Time direction (hop) and citation direction
//     (trailRel, elsewhere) are different channels - this module never imports either.
//
// ASCII only in code + comments (Hebrew UI strings via labelHe are allowed, but we
// avoid the geresh/gershayim marks the cp1252 toolchain mangles - see normalizeRef.ts).

import { norm } from "./taxonomy.ts";
import { commentaryEra } from "./era.ts";
import { getSourceType, tanakhSection } from "./typeSystem.ts";
import { parseCanonicalRef } from "./normalizeRef.ts";

export type EraTier =
  | "foundation"
  | "chazal"
  | "geonim"
  | "rishonim"
  | "acharonim"
  | "modern"
  | "unknown";

export type EraConfidence =
  | "exact"
  | "approximate"
  | "range"
  | "era-only"
  | "foundation"
  | "unknown";

export type TimeDirection = "back" | "forward" | "contemporary" | "unknown";

export type MagnitudeBucket =
  | "none"
  | "tiny"
  | "small"
  | "medium"
  | "large"
  | "epic"
  | "origin"
  | "unknown";

export interface EraInfo {
  tier: EraTier;
  isFoundational: boolean;
  approxYear: number | null; // CE, negative = BCE. null unless exact/approximate. NEVER for foundation.
  rangeStart: number | null;
  rangeEnd: number | null;
  yearLabel: string | null; // "1040-1105" | "~500 CE" | null
  confidence: EraConfidence;
  label: string; // EN era name
  labelHe: string; // HE era name (textContent only)
  colorVar: string; // a CSS custom property name, e.g. "--era-rishonim"
  source:
    | "ref-shape-foundation"
    | "sefaria-compdate"
    | "commentator-override"
    | "era-ts-tier"
    | "type-era"
    | "fallback";
}

export interface HopInfo {
  deltaYears: number | null; // null whenever either side lacks a real exact/approximate year
  direction: TimeDirection;
  magnitudeBucket: MagnitudeBucket;
  displayPrecision: EraConfidence; // weakest(a.confidence, b.confidence)
  crossesFoundation: boolean;
  sameTier: boolean;
  note: string;
}

export interface EraInput {
  category: string | null;
  commentator: string | null;
  ref: string | null;
  compDate?: [number] | [number, number] | null;
  sefariaEra?: string | null;
}

// ---------------------------------------------------------------------------
// Per-tier display metadata (the lightness-ramp tokens live in tokens.css).
// ---------------------------------------------------------------------------

interface TierMeta {
  label: string;
  labelHe: string;
  colorVar: string;
  order: number; // chronological rank; -1 = off the dated axis (unknown)
}

const TIER_META: Record<EraTier, TierMeta> = {
  foundation: { label: "Origin", labelHe: "מקרא", colorVar: "--era-foundation", order: 0 },
  chazal: { label: "Chazal", labelHe: "חכמים", colorVar: "--era-chazal", order: 1 },
  geonim: { label: "Geonim", labelHe: "גאונים", colorVar: "--era-geonim", order: 2 },
  rishonim: { label: "Rishonim", labelHe: "ראשונים", colorVar: "--era-rishonim", order: 3 },
  acharonim: { label: "Acharonim", labelHe: "אחרונים", colorVar: "--era-acharonim", order: 4 },
  modern: { label: "Modern", labelHe: "עת חדשה", colorVar: "--era-modern", order: 5 },
  unknown: { label: "Unknown era", labelHe: "לא ידוע", colorVar: "--era-unknown", order: -1 },
};

// ---------------------------------------------------------------------------
// Curated overrides (the only per-named-work / per-author dating). Each entry
// carries its OWN tier - never derived from a possibly-null era.ts lookup. A
// `year` is a single approximate CE point used for axis placement + delta math;
// `yearLabel` is the human-honest string (lifespan or span). Broad anonymous
// corpora carry a range only (no single year -> no fabricated delta).
// Ship-gate: verify entry-by-entry against Sefaria compDate before 1b ships.
// ---------------------------------------------------------------------------

interface TimelineEntry {
  tier: EraTier;
  year?: number;
  rangeStart?: number;
  rangeEnd?: number;
  yearLabel?: string;
  confidence: EraConfidence;
  curatorNote?: string;
}

const TIMELINE_OVERRIDES: Readonly<Record<string, TimelineEntry>> = {
  // Geonim (so era.ts "classic" never blanket-maps these into Rishonim)
  "saadia gaon": { tier: "geonim", year: 920, rangeStart: 882, rangeEnd: 942, yearLabel: "882-942", confidence: "approximate" },
  "rabbeinu chananel": { tier: "geonim", year: 1020, rangeStart: 990, rangeEnd: 1053, yearLabel: "c. 990-1053", confidence: "approximate" },

  // Rishonim (persons)
  rashi: { tier: "rishonim", year: 1090, rangeStart: 1040, rangeEnd: 1105, yearLabel: "1040-1105", confidence: "approximate" },
  rashbam: { tier: "rishonim", year: 1120, rangeStart: 1085, rangeEnd: 1158, yearLabel: "c. 1085-1158", confidence: "approximate" },
  ramban: { tier: "rishonim", year: 1230, rangeStart: 1194, rangeEnd: 1270, yearLabel: "1194-1270", confidence: "approximate" },
  "ibn ezra": { tier: "rishonim", year: 1140, rangeStart: 1089, rangeEnd: 1167, yearLabel: "1089-1167", confidence: "approximate" },
  radak: { tier: "rishonim", year: 1200, rangeStart: 1160, rangeEnd: 1235, yearLabel: "c. 1160-1235", confidence: "approximate" },
  sforno: { tier: "rishonim", year: 1510, rangeStart: 1475, rangeEnd: 1549, yearLabel: "c. 1475-1549", confidence: "approximate" },
  "rabbeinu bahya": { tier: "rishonim", year: 1300, rangeStart: 1255, rangeEnd: 1340, yearLabel: "c. 1255-1340", confidence: "approximate" },

  // Named corpora with a real curated date (most-specific wins over a generic default)
  mishnah: { tier: "chazal", year: 200, yearLabel: "~200 CE", confidence: "approximate", curatorNote: "Mishnah redacted c. 200 CE" },
  sifrei: { tier: "chazal", rangeStart: 200, rangeEnd: 300, yearLabel: "~3rd c. CE", confidence: "range" },
  "midrash tanchuma": { tier: "chazal", rangeStart: 500, rangeEnd: 800, yearLabel: "~6th-8th c. CE", confidence: "range" },
  "bamidbar rabbah": { tier: "rishonim", year: 1200, yearLabel: "~1200 CE", confidence: "approximate", curatorNote: "late midrashic compilation; Sefaria compDate ~1200" },

  // Acharonim
  "kli yakar": { tier: "acharonim", year: 1590, rangeStart: 1550, rangeEnd: 1619, yearLabel: "c. 1550-1619", confidence: "approximate" },
  malbim: { tier: "acharonim", year: 1845, rangeStart: 1809, rangeEnd: 1879, yearLabel: "1809-1879", confidence: "approximate" },

  // Modern-Orthodox (kept by content policy)
  "rav hirsch": { tier: "modern", year: 1850, rangeStart: 1808, rangeEnd: 1888, yearLabel: "1808-1888", confidence: "approximate" },
};

// Names era.ts files under "classic" that are really Geonim, not Rishonim.
const GEONIM_EXCEPTION: ReadonlySet<string> = new Set(["saadia gaon", "rabbeinu chananel"]);

// Sefaria index `era` code -> our tier. (foundation never arrives here - Step 0 owns it.)
const SEFARIA_ERA_TIER: Readonly<Record<string, EraTier>> = {
  T: "chazal", // Tannaim
  A: "chazal", // Amoraim
  GN: "geonim",
  RI: "rishonim",
  AH: "acharonim",
  CO: "modern",
  Tanakh: "foundation",
};

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

function build(tier: EraTier, opts: Partial<EraInfo> & { source: EraInfo["source"] }): EraInfo {
  const meta = TIER_META[tier];
  return {
    tier,
    isFoundational: tier === "foundation",
    approxYear: opts.approxYear ?? null,
    rangeStart: opts.rangeStart ?? null,
    rangeEnd: opts.rangeEnd ?? null,
    yearLabel: opts.yearLabel ?? null,
    confidence: opts.confidence ?? "unknown",
    label: meta.label,
    labelHe: meta.labelHe,
    colorVar: meta.colorVar,
    source: opts.source,
  };
}

const FOUNDATION = (): EraInfo => build("foundation", { confidence: "foundation", source: "ref-shape-foundation" });
const UNKNOWN = (): EraInfo => build("unknown", { confidence: "unknown", source: "fallback" });
const tierOnly = (tier: EraTier): EraInfo => build(tier, { confidence: "era-only", source: "era-ts-tier" });

// ---------------------------------------------------------------------------
// Step 0 - Foundation via ref shape (theology guard).
// ---------------------------------------------------------------------------

function isFoundationRef(ref: string | null): boolean {
  if (!ref) return false;
  if (/ on /i.test(ref)) return false; // a commentary ref is never Foundation
  const parsed = parseCanonicalRef(ref);
  if (parsed) return parsed.baseWorkTitle === null && tanakhSection(parsed.workTitle) !== null;
  // No parseable numeric tail (e.g. a whole-book ref) - test the raw token.
  return tanakhSection(ref.trim()) !== null;
}

// ---------------------------------------------------------------------------
// Step 1 - commentator / named-work override (direct -> "X on Book" head -> longest-prefix).
// ---------------------------------------------------------------------------

function refHead(ref: string | null): string | null {
  if (!ref || !/ on /i.test(ref)) return null;
  return ref.split(/ on /i)[0].trim();
}

function overrideLookup(commentator: string | null, ref: string | null): TimelineEntry | null {
  const direct = commentator ? TIMELINE_OVERRIDES[norm(commentator)] : undefined;
  if (direct) return direct;

  const head = refHead(ref);
  if (head) {
    const byHead = TIMELINE_OVERRIDES[norm(head)];
    if (byHead) return byHead;
  }
  // EXACT match only (direct name + "X on Book" head). A loose longest-prefix match
  // would let a short key (e.g. "mishnah", ~200 CE) wrongly claim a distinct longer
  // work that merely shares the leading token (e.g. "Mishnah Berurah", ~1907 CE) and
  // silently mis-date it by ~1700 years - an ACCURACY-LOCK violation. Un-overridden
  // works fall through to the real Sefaria compDate (Step 2), which is correct.
  return null;
}

function fromOverride(ov: TimelineEntry): EraInfo {
  return build(ov.tier, {
    approxYear: ov.year ?? null,
    rangeStart: ov.rangeStart ?? null,
    rangeEnd: ov.rangeEnd ?? null,
    yearLabel: ov.yearLabel ?? null,
    confidence: ov.confidence,
    source: "commentator-override",
  });
}

// ---------------------------------------------------------------------------
// Step 2 - Sefaria compDate / era (date the WORK).
// ---------------------------------------------------------------------------

function fmtYear(y: number): string {
  return y < 0 ? `${-y} BCE` : `${y} CE`;
}

function tierHintFromCategory(category: string | null): EraTier | null {
  if (!category) return null;
  const key = getSourceType(category).key;
  if (key === "talmud" || key === "targum") return "chazal";
  return null;
}

function fromSefaria(compDate: [number] | [number, number], era: string | null, category: string | null): EraInfo {
  const tier = (era && SEFARIA_ERA_TIER[era]) || tierHintFromCategory(category) || "unknown";
  if (compDate.length === 1) {
    const y = compDate[0];
    return build(tier, { approxYear: y, yearLabel: `~${fmtYear(y)}`, confidence: "approximate", source: "sefaria-compdate" });
  }
  const [a, b] = compDate;
  return build(tier, {
    rangeStart: a,
    rangeEnd: b,
    yearLabel: `${a < 0 ? -a + " BCE" : a}-${fmtYear(b)}`,
    confidence: "range",
    source: "sefaria-compdate",
  });
}

// ---------------------------------------------------------------------------
// Step 4 - category/type era (only for corpora that genuinely share an era).
// ---------------------------------------------------------------------------

function typeEraFor(category: string | null): EraInfo | null {
  if (!category) return null;
  const key = getSourceType(category).key;
  if (key === "targum") {
    return build("chazal", { rangeStart: 100, rangeEnd: 200, yearLabel: "~100-200 CE", confidence: "range", source: "type-era" });
  }
  if (key === "talmud") {
    return build("chazal", { rangeStart: 200, rangeEnd: 500, yearLabel: "~200-500 CE", confidence: "range", source: "type-era" });
  }
  // Midrash / Halacha / Thought span centuries - never a category default. Commentary
  // is owned by Step 3 (era.ts). Tanakh is owned by Step 0. Other -> unknown.
  return null;
}

function isGeonim(name: string | null, ref: string | null): boolean {
  if (name && GEONIM_EXCEPTION.has(norm(name))) return true;
  const head = refHead(ref);
  return head ? GEONIM_EXCEPTION.has(norm(head)) : false;
}

// ---------------------------------------------------------------------------
// eraOf - the resolution cascade (order is load-bearing).
// ---------------------------------------------------------------------------

export function eraOf(input: EraInput): EraInfo {
  const { category, commentator, ref } = input;

  // STEP 0 - Foundation (theology guard, first, no exceptions).
  if (isFoundationRef(ref)) return FOUNDATION();

  // STEP 1 - curated override.
  const ov = overrideLookup(commentator, ref);
  if (ov) return fromOverride(ov);

  // THEOLOGY DRIFT GUARD (surfaced by the adversarial council 2026-06-22): a base-form ref
  // (no " on ") that Sefaria still categorizes as "Tanakh" but whose title Step 0 did NOT
  // recognize - e.g. a future Sefaria title drift like "Malachi, The Prophet" - must NEVER be
  // academically dated. Refuse compDate so a drifted Tanakh book can never become a dated
  // "prophet"; it falls to an honest neutral (era.ts tier for a mis-categorized commentary,
  // or "unknown era" for a genuinely unrecognized base text).
  const baseFormTanakh = ref != null && !/ on /i.test(ref) && category === "Tanakh";

  // STEP 2 - Sefaria compDate / era (date the WORK), but NEVER for a base-form Tanakh ref.
  if (input.compDate && input.compDate.length && !baseFormTanakh) {
    return fromSefaria(input.compDate, input.sefariaEra ?? null, category);
  }

  // STEP 3 - commentator tier from era.ts (no year).
  const nameForEra = commentator ?? refHead(ref);
  if (nameForEra) {
    const e = commentaryEra(nameForEra, ref ?? undefined);
    if (e === "classic") return tierOnly(isGeonim(nameForEra, ref) ? "geonim" : "rishonim");
    if (e === "later") return tierOnly("acharonim");
  }

  // STEP 4 - category/type era.
  const te = typeEraFor(category);
  if (te) return te;

  // STEP 5 - honest fallback.
  return UNKNOWN();
}

// ---------------------------------------------------------------------------
// hop - compares two consecutive trail nodes by TIME only.
// ---------------------------------------------------------------------------

const CONFIDENCE_RANK: Record<EraConfidence, number> = {
  exact: 5,
  approximate: 4,
  range: 3,
  "era-only": 2,
  foundation: 1,
  unknown: 0,
};

function weakest(a: EraConfidence, b: EraConfidence): EraConfidence {
  return CONFIDENCE_RANK[a] <= CONFIDENCE_RANK[b] ? a : b;
}

function magBucket(abs: number): MagnitudeBucket {
  if (abs < 100) return "tiny";
  if (abs < 300) return "small";
  if (abs < 700) return "medium";
  if (abs < 1500) return "large";
  return "epic";
}

function directionByTier(a: EraTier, b: EraTier): TimeDirection {
  const oa = TIER_META[a].order;
  const ob = TIER_META[b].order;
  if (oa < 0 || ob < 0) return "unknown";
  if (ob > oa) return "forward";
  if (ob < oa) return "back";
  return "contemporary";
}

function tierGapBucket(a: EraTier, b: EraTier): MagnitudeBucket {
  const oa = TIER_META[a].order;
  const ob = TIER_META[b].order;
  if (oa < 0 || ob < 0) return "unknown";
  const d = Math.abs(ob - oa);
  if (d === 0) return "none";
  if (d === 1) return "small";
  if (d === 2) return "medium";
  return "large";
}

function foundationNote(dir: TimeDirection): string {
  if (dir === "forward") return "stepping off the wellspring";
  if (dir === "back") return "back to the source text";
  return "within the wellspring";
}

export function hop(a: EraInfo, b: EraInfo): HopInfo {
  const crossesFoundation = a.isFoundational || b.isFoundational;
  const sameTier = a.tier === b.tier && a.tier !== "unknown" && a.tier !== "foundation";
  const displayPrecision = weakest(a.confidence, b.confidence);

  // H1 - any hop touching Foundation: un-quantified origin, never a number.
  if (crossesFoundation) {
    const direction: TimeDirection =
      a.isFoundational && b.isFoundational ? "contemporary" : a.isFoundational ? "forward" : "back";
    return {
      deltaYears: null,
      direction,
      magnitudeBucket: "origin",
      displayPrecision,
      crossesFoundation: true,
      sameTier,
      note: foundationNote(direction),
    };
  }

  // Order-robust decision (H1 Foundation already returned above). The three branches are
  // mutually exclusive by construction, so reordering them cannot change behavior (the
  // adversarial council flagged the old implicit H2-before-H0 dependency as a latent smell):
  //   H3 = both sides carry a real exact/approximate year -> the ONLY path that shows a number.
  //   H2 = same real dated tier without two usable years -> "same era".
  //   H0 = different tiers without two usable years -> honest tier hop, no fabricated number.
  const ay = a.approxYear;
  const by = b.approxYear;
  const strongPrecision = displayPrecision === "exact" || displayPrecision === "approximate";

  if (ay !== null && by !== null && strongPrecision) {
    const delta = by - ay;
    const band = displayPrecision === "approximate" ? 100 : 30;
    const direction: TimeDirection = delta > band ? "forward" : delta < -band ? "back" : "contemporary";
    const magnitudeBucket: MagnitudeBucket = direction === "contemporary" ? "none" : magBucket(Math.abs(delta));
    return {
      deltaYears: Math.round(delta),
      direction,
      magnitudeBucket,
      displayPrecision,
      crossesFoundation: false,
      sameTier,
      note:
      direction === "contemporary"
        ? sameTier
          ? `same era (${a.label})`
          : "around the same time" // near-band but DIFFERENT tiers - never claim "same era (X)"
        : `~${Math.abs(delta)} years ${direction}`,
    };
  }

  if (sameTier) {
    return {
      deltaYears: null,
      direction: "contemporary",
      magnitudeBucket: "none",
      displayPrecision,
      crossesFoundation: false,
      sameTier: true,
      note: `same era (${a.label})`,
    };
  }

  const direction = directionByTier(a.tier, b.tier);
  const verb = direction === "back" ? "back to" : direction === "forward" ? "forward to" : "across to";
  return {
    deltaYears: null,
    direction,
    magnitudeBucket: tierGapBucket(a.tier, b.tier),
    displayPrecision,
    crossesFoundation: false,
    sameTier,
    note: `from the ${a.label} ${verb} the ${b.label}`,
  };
}
